import React from 'react';
import useAlphaSnackbar from 'hooks/useAlphaSnackbar';
import useLog from 'hooks/useLog';
import useProfile from 'hooks/useProfile';
import useSwitchAccount from 'hooks/useSwitchAccount';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import browserHistory from 'services/history/browserHistory';
import { actions as creditActions } from 'store/creditFacility/reducer';
import { actions } from 'store/user/user.reducer';
import t from 'utils/translationHelper';

import { AccountDto } from '@alpha/auth-dtos';
import { Box, Button } from '@alpha/ui-lib/ui/external';
import { APTooltip } from '@alpha/ui-lib/ui/Tooltip/APTooltip';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import corpayVIcon from '../../../../../assets/CorpayV.png';

import useStyles from './Account.styles';

interface IProps {
  account: AccountDto;
  filterAccounts: string;
  setFilterAccounts?: (val: string) => void;
  onClose?: (event: any) => void;
  setLoading?: (val: boolean) => void;
}

const Account: React.FC<IProps> = ({
  account, filterAccounts, onClose, setFilterAccounts, setLoading,
}: IProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { updateUserProfile } = useProfile();
  const { name, id, corpayMigrationStartDate, corpayMigrationEndDate } = account;
  const {
    updateCurrentAccount, currentAccount,
    mapPageToPath,
  } = useSwitchAccount();
  const sb = useAlphaSnackbar();
  const { logEvent } = useLog();
  const classes = useStyles();

  const getBasePath = (fullPath: string): string => {
    try {
      const newPath = fullPath.split('/', 3);
      return newPath.join('/');
    } catch {
      return '/app';
    }
  };

  const handleButtonClick = (): void => {
    (async () => {
      if (setLoading) { setLoading(true); }
      await updateCurrentAccount(account);
      await updateUserProfile();

      browserHistory.push(mapPageToPath('DASHBOARD')
        || getBasePath(location.pathname));

      if (setFilterAccounts) { setFilterAccounts(''); }
      if (onClose) { onClose(false); }
      dispatch(actions.updateKey());
      dispatch(creditActions.clearCreditFacility());
      dispatch(creditActions.loadCreditFacility());
      if (setLoading) { setLoading(false); }
      if (localStorage.getItem('i18nextLng') !== 'de') {
        sb.trigger(`${t('switched_accounts_to')} ${account.name}`, 'success');
      } else {
        sb.trigger(`${t('switched_accounts_to1')} ${account.name} ${t('switched_accounts_to2')}`, 'success');
      }
      logEvent({ action: 'Switched account' });
    })();
  };

  const isInMigrationWindow = () => {
    if (corpayMigrationStartDate == null || corpayMigrationEndDate == null) {
      return false;
    }
    
    return DateTime.now() >= DateTime.fromISO(corpayMigrationStartDate)
      && DateTime.now() <= DateTime.fromISO(corpayMigrationEndDate);
  };

  const migrationStatusKey: 'account_migration_in_progress' | 'account_migrated' | null = (() => {
    if (isInMigrationWindow()) return 'account_migration_in_progress';
    if (DateTime.now() >= DateTime.fromISO(corpayMigrationEndDate)) return 'account_migrated';
    return null;
  })();

  return (
    <>
      {
        name.toLowerCase().includes(filterAccounts.toLowerCase())
          && currentAccount
          ? (
            <Box className={id === currentAccount.id
              ? classes.currentAccountWrapper : classes.accountWrapper}
            >
              <Box className={classes.accountDetails}>
                <Typography className={classes.accountName}>{name}</Typography>
                {migrationStatusKey ? (
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
                    <div style={{
                      backgroundColor: '#3D1629',
                      height: '21px',
                      width: '21px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                    }}
                    >
                      <img src={corpayVIcon} alt="Corpay" style={{ height: '9px' }} />
                    </div>
                    <Typography className={classes.accountDescription}>{t(migrationStatusKey)}</Typography>
                  </div>
                ) : null}
              </Box>
              {
                currentAccount
                  && id === currentAccount.id
                  ? (
                    <Box className={classes.selectedAccount}>
                      <span>{t('selected')}</span>
                      <FontAwesomeIcon
                        className={classes.tickIcon}
                        icon={faCheckCircle as IconProp}
                      />
                    </Box>
                  )
                  : (
                    <Button
                      data-testid={`test-account-${id}`}
                      onClick={() => { handleButtonClick(); }}
                      className={classes.button}
                    >
                      <span>{t('view')}</span>
                      {(DateTime.now() > DateTime.fromISO('2026-04-05T00:00:00Z')
                      || process.env.REACT_APP_ENV !== 'prod')
                      && DateTime.fromISO(corpayMigrationStartDate) <= DateTime.now() ? (
                        <APTooltip
                          title={t('account_is_only_available_for_read_only_reporting_access')}
                          arrow
                        >
                          <div style={{ marginLeft: '6px' }}>
                            <FontAwesomeIcon
                              icon={faCircleInfo as IconProp}
                              style={{ color: '#3E8CC1', cursor: 'pointer' }}
                            />
                          </div>
                        </APTooltip>
                        ) : null}
                    </Button>
                  )
              }
            </Box>
          )
          : <></>
      }
    </>
  );
};

export default Account;
